import { Badge, Box, Button, Divider, Flex, HStack, Icon, Text } from '@chakra-ui/react'
import { FaMapMarkerAlt } from 'react-icons/fa'
import type { PrescriberLocationProfile } from '../../../types/location'

type JoinedLocationProfileCardProps = {
  profile: PrescriberLocationProfile
  isConfirmingLeave: boolean
  isLeaveSubmitting: boolean
  onEdit: () => void
  onRequestLeave: () => void
  onConfirmLeave: () => void
  onCancelLeave: () => void
}

export const JoinedLocationProfileCard = ({
  profile,
  onCancelLeave,
  isConfirmingLeave,
  isLeaveSubmitting,
  onEdit,
  onRequestLeave,
  onConfirmLeave,
}: JoinedLocationProfileCardProps) => {
  const locationName = profile.name || 'Unknown location'
  const isDefault = profile.isDefault

  return (
    <Box border="1px" borderColor="gray.200" borderRadius="md" bg="white" overflow="hidden">
      <Flex justify="space-between" align="center" bg="blue.50" px={4} py={3}>
        <HStack spacing={2}>
          <Icon as={FaMapMarkerAlt} color="red.500" boxSize={4} />
          <Text fontWeight="bold" fontSize="sm" color="blue.800">
            {locationName}
          </Text>
          {isDefault ? (
            <Badge colorScheme="green" fontSize="2xs" px={2} py={0.5} borderRadius="sm">
              Default
            </Badge>
          ) : null}
        </HStack>

        {isConfirmingLeave ? (
          <HStack spacing={3}>
            <Text fontSize="sm" color="red.600" fontWeight="bold">
              Leave this location?
            </Text>
            <Button
              size="sm"
              bg="red.600"
              color="white"
              _hover={{ bg: 'red.700' }}
              onClick={onConfirmLeave}
              isLoading={isLeaveSubmitting}
            >
              Yes, leave
            </Button>
            <Button
              size="sm"
              variant="outline"
              bg="white"
              onClick={onCancelLeave}
              isDisabled={isLeaveSubmitting}
            >
              Cancel
            </Button>
          </HStack>
        ) : (
          <HStack spacing={4}>
            <Button
              variant="link"
              color="blue.600"
              fontSize="sm"
              fontWeight="medium"
              onClick={onEdit}
            >
              Edit
            </Button>
            {!isDefault ? (
              <Button
                variant="outline"
                colorScheme="red"
                size="sm"
                bg="white"
                onClick={onRequestLeave}
              >
                Leave
              </Button>
            ) : null}
          </HStack>
        )}
      </Flex>

      <Divider borderColor="gray.200" />

      <Flex px={4} py={4} justify="space-between" align="center">
        <Text fontSize="sm" color="gray.600" flex={1}>
          Provider No:{' '}
          <Text as="span" fontWeight="bold" color="gray.900">
            {profile.providerNumber?.trim() || '—'}
          </Text>
        </Text>
        <Text fontSize="sm" color="gray.600" flex={1}>
          Phone:{' '}
          <Text as="span" fontWeight="bold" color="gray.900">
            {profile.phoneNumber || '—'}
          </Text>
        </Text>
        <Text fontSize="sm" color="gray.600" flex={1}>
          Email:{' '}
          <Text as="span" fontWeight="bold" color="gray.900">
            {profile.email || '—'}
          </Text>
        </Text>
      </Flex>
    </Box>
  )
}
